import { createApiRef } from '@backstage/core-plugin-api';
import { Flag } from './entities/Flag';
import { FlagDetailsUpdate } from './entities/FlagDetailsUpdate';
import { FlagState, OverrideResult } from './entities/FlagState';
import { FeatureTicketSuggestion, JiraAssignee, JiraTicketKind, JiraTickets } from './entities/JiraTicket';
import { NewFlag } from './entities/NewFlag';
import { PrConfirmation } from './entities/PrConfirmation';
import { PrKind } from './entities/PrKind';
import { Stage } from './entities/Stage';
import { TierTarget } from './entities/TierTarget';

export interface FeatureFlagRolloutApi {
  getTierList(): Promise<TierTarget[]>;
  listFlags(): Promise<Flag[]>;
  getFlag(key: string): Promise<Flag | undefined>;
  addFlag(flag: NewFlag): Promise<Flag>;
  updateFlagDetails(key: string, update: FlagDetailsUpdate): Promise<Flag>;
  setStage(key: string, stage: Stage, options: { enteredAt?: string; reason?: string }): Promise<Flag>;
  setTargetDone(key: string, targetId: string, done: boolean): Promise<Flag>;
  markTargetsDone(key: string, targetIds: string[]): Promise<Flag>;
  setPrUrl(key: string, kind: PrKind, url: string): Promise<Flag>;
  /** `at` back-dates the confirmation, for a deploy whose time GitHub couldn't tell. */
  setPrConfirmation(key: string, kind: PrKind, confirmation: PrConfirmation, confirmed: boolean, at?: string): Promise<Flag>;
  getFlagState(env: string, flagKey: string, tonkeanToken: string): Promise<FlagState>;
  /** Every Tonkean environment the backend knows, not only the ones in the tier list. */
  listEnvironmentNames(): Promise<string[]>;
  /**
   * Ticks the explicit-off check as passed, or unticks it so it runs again. `override` passes it although some
   * environments couldn't be read, with the user's reason.
   */
  setOffCheckPassed(key: string, passed: boolean, override?: { unchecked: string[]; reason: string }): Promise<Flag>;
  /** For backend-only flags: Delete PRs then finishes on the backend PR alone. */
  setAppPrNotNeeded(key: string, notNeeded: boolean): Promise<Flag>;
  /** Sets the flag to true for the target in Tonkean, then marks the target done. */
  applyOverride(key: string, target: TierTarget, tonkeanToken: string): Promise<{ flag: Flag; result: OverrideResult }>;
  /**
   * Sets the flag to true for one off-check row: a project, or the whole environment when `projectId` is null.
   * A matching tier target is marked done.
   */
  setOverrideTrue(
    key: string,
    row: { env: string; projectId: string | null; label: string; targetId?: string },
    tonkeanToken: string,
  ): Promise<{ flag: Flag; result: OverrideResult }>;
  /**
   * Opens the Dev CI or default-true PR from main with the backend's GitHub token and links it.
   * `notice` is set when the file is already in that state, so no PR was opened.
   */
  openPreparedPr(key: string, kind: 'devCi' | 'defaultTrue'): Promise<{ flag: Flag; notice: string | null }>;
  /** Whether the backend's GitHub token is set, and which linked-PR repos it can't read. */
  getGithubAccess(): Promise<{ configured: boolean; unreadable: string[] }>;
  /** Whether the backend's Jira login is set and accepted, and whose it is. */
  getJiraAccess(): Promise<{ configured: boolean; valid: boolean; name: string | null }>;
  /** The flag's linked turn-on and removal tickets, as Jira reports them now. */
  getJiraTickets(key: string): Promise<JiraTickets>;
  /**
   * Whom new tickets are assigned to: the Jira user with your Tonkean email (read through one of your Tonkean
   * tokens), or else the backend's Jira account, with `fallbackReason` saying why.
   */
  getJiraAssignee(): Promise<JiraAssignee>;
  /**
   * Creates the ticket as a sub-task of the feature ticket, with the backend's Jira login, assigns it to
   * `getJiraAssignee()`, and links it.
   */
  createJiraTicket(key: string, kind: JiraTicketKind): Promise<Flag>;
  moveJiraTicket(key: string, kind: JiraTicketKind | 'feature', status: string): Promise<Flag>;
  /** Jira people matching a name or email, for picking an owner; only those whose email Jira shows. */
  searchJiraUsers(query: string): Promise<{ name: string; email: string; avatarUrl: string | null }[]>;
  /** TNKN issues whose Feature Flag field names the flag, best feature ticket first. */
  suggestFeatureTickets(flagKey: string): Promise<FeatureTicketSuggestion[]>;
  /** Environments the signed-in user marked "no access". */
  getNoAccessEnvironments(): Promise<string[]>;
  setNoAccess(env: string, noAccess: boolean): Promise<string[]>;
}

export const featureFlagRolloutApiRef = createApiRef<FeatureFlagRolloutApi>({
  id: 'plugin.feature-flag-rollout.service',
});
